import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { timelineApi } from '../utils/timelineApi';

const ActivityFeedContainer = styled.div`
  width: 100%;
  margin-top: 0;
`;



const SeeAllButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: white;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: fadeIn 0.4s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TimelineItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 12px;
  opacity: 0;
  animation: fadeInItem 0.4s ease-in forwards;
  
  @keyframes fadeInItem {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  ${props => props.index !== undefined && `
    animation-delay: ${props.index * 0.05}s;
  `}
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const TimelineItemType = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.color || '#FF2300'};
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const TimelineItemStatus = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 8px;
`;

const TimelineItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
`;

const TimelineItemDescription = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const TimelineItemTime = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TimelineItemAction = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${props => props.color || '#FF2300'};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.color || '#FF2300'};
    color: ${props => props.color || '#FF2300'};
  }
`;

const SimpleEmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
  color: rgba(255, 255, 255, 0.7);
`;

const PlusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::before {
    content: '+';
    font-size: 24px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 300;
  }
`;

const EmptyStateText = styled.div`
  flex: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: left;
  padding: 60px 0;
`;

const EmptyStateTitle = styled.h3`
  color: white;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 300px;
`;

const EmptyStateButton = styled.button`
  background: #17A2B8;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 12px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #20B2AA;
  }
`;

const ActivityFeed = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    loadTimeline();
  }, []);

  const loadTimeline = async () => {
    try {
      const response = await timelineApi.getTimeline();
      
      if (response && response.success) {
        // Filter out overdue items and only show upcoming items (Today, Tomorrow, This Week, Next Week, or future dates)
        const items = (response.timeline || []).filter(item => {
          const dateGroup = item.dateGroup || '';
          // Exclude overdue and past items
          return dateGroup !== 'Overdue' && dateGroup !== 'Past' && 
                 (dateGroup === 'Today' || dateGroup === 'Tomorrow' || 
                  dateGroup === 'This Week' || dateGroup === 'Next Week' ||
                  (item.date && new Date(item.date) >= new Date(new Date().setHours(0, 0, 0, 0))));
        });
        
        // Show only first 10 upcoming items on the main page
        setTimeline(items.slice(0, 10));
      } else {
        setTimeline([]);
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load timeline:', error);
      setTimeline([]);
      setHasLoaded(true);
    }
  };

  const getEmptyStateContent = () => {
    return {
      title: 'No upcoming events',
      description: 'Add shows, releases, tasks, or travel to see them in your timeline'
    };
  };

  const getTypeLabel = (type, subType) => {
    const labels = {
      'show': 'SHOW',
      'release': 'RELEASE',
      'task': 'TASK',
      'flight': 'FLIGHT',
      'hotel': 'HOTEL',
      'transport': 'TRANSPORT',
      'ticket-update': 'TICKET UPDATE'
    };
    
    return labels[type] || 'EVENT';
  };

  const getStatusLabel = (status, subType) => {
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'pending') return 'Pending';
    if (status === 'due-soon') return 'Due Soon';
    if (status === 'overdue') return 'Overdue';
    if (status === 'update') return 'Update';
    return '';
  };

  const renderTimelineItem = (item, index) => {
    const itemColor = item.color || '#FF2300';
    const typeLabel = getTypeLabel(item.type, item.subType);
    const statusLabel = getStatusLabel(item.status, item.subType);
    
    return (
      <TimelineItem 
        key={item.id}
        index={index}
        onClick={() => item.actionUrl && navigate(item.actionUrl)}
      >
        <div style={{ flex: 1 }}>
          <TimelineItemType color={itemColor}>
            {typeLabel}
            {statusLabel && <TimelineItemStatus>• {statusLabel}</TimelineItemStatus>}
          </TimelineItemType>
          <TimelineItemTitle>{item.title}</TimelineItemTitle>
          <TimelineItemDescription>{item.description}</TimelineItemDescription>
          {item.timeContext && (
            <TimelineItemTime>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{item.timeContext}</span>
            </TimelineItemTime>
          )}
          {item.actionLabel && (
            <TimelineItemAction 
              color={itemColor}
              onClick={(e) => {
                e.stopPropagation();
                if (item.actionUrl) navigate(item.actionUrl);
              }}
            >
              {item.actionLabel}
            </TimelineItemAction>
          )}
        </div>
      </TimelineItem>
    );
  };

  return (
    <ActivityFeedContainer>
      {timeline.length === 0 && hasLoaded ? (
        <EmptyState>
          <EmptyStateTitle>{getEmptyStateContent().title}</EmptyStateTitle>
          <EmptyStateDescription>{getEmptyStateContent().description}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <ActivityList>
          {timeline.map((item, index) => renderTimelineItem(item, index))}
        </ActivityList>
      )}
    </ActivityFeedContainer>
  );
};

export default ActivityFeed; 